import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { KeyIcon } from '@heroicons/react/24/outline';
import './ChildSignInCard.css';

const names = (children) => {
  const first = children.map(c => c.firstName);
  return first.length <= 1 ? first.join('') : `${first.slice(0, -1).join(', ')} and ${first[first.length - 1]}`;
};

// On the home page of anyone with children on their account (parents, and
// staff who are parents): a code their children use to sign in from home.
// It lasts until the parent turns it off or makes a new one.
export default function ChildSignInCard() {
  const [state, setState] = useState(null);
  const [confirming, setConfirming] = useState(null); // 'new' | 'off'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get('/api/auth/share-code').then(res => setState(res.data)).catch(() => setState(null));
  }, []);

  if (!state || state.children.length === 0) return null;

  const signInUrl = `${window.location.origin}/child-login`;
  const who = names(state.children);

  const change = async (action) => {
    setBusy(true);
    setError(null);
    try {
      const res = action === 'off'
        ? await axios.delete('/api/auth/share-code')
        : await axios.post('/api/auth/generate-share-code');
      setState(res.data);
      setConfirming(null);
      setCopied(false);
    } catch {
      setError(action === 'off' ? 'Could not turn the code off. Please try again.' : 'Could not make a code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const text = `Sign in to the club app at ${signInUrl} with your first name, last name and the code ${state.shareCode}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setError('Could not copy. Write the code down instead.');
    }
  };

  return (
    <section className="child-signin" aria-labelledby="child-signin-title">
      <h2 id="child-signin-title" className="child-signin__title">
        <KeyIcon className="child-signin__icon" aria-hidden="true" />
        Let {who} sign in at home
      </h2>

      {state.shareCode ? (
        <>
          <p className="child-signin__code" aria-label={`Sign in code ${state.shareCode}`}>{state.shareCode}</p>
          <ol className="child-signin__steps">
            <li>Open <strong>{signInUrl.replace(/^https?:\/\//, '')}</strong></li>
            <li>Tap <strong>Gymnast signing in with a code?</strong></li>
            <li>Enter their first name, last name and this code</li>
          </ol>
          <p className="child-signin__note">
            The code works for {who} only, and keeps working until you turn it off or make a new one.
          </p>
          {confirming ? (
            <div className="child-signin__confirm" role="alert">
              <p>
                {confirming === 'new'
                  ? `The current code will stop working and ${who} will be signed out until they use the new one.`
                  : `The code will stop working and ${who} will be signed out.`}
              </p>
              <div className="child-signin__actions">
                <button type="button" className="bk-btn bk-btn--primary bk-btn--sm" disabled={busy} onClick={() => change(confirming)}>
                  {confirming === 'new' ? 'Make new code' : 'Turn off code'}
                </button>
                <button type="button" className="bk-btn bk-btn--outline bk-btn--sm" disabled={busy} onClick={() => setConfirming(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="child-signin__actions">
              <button type="button" className="bk-btn bk-btn--primary bk-btn--sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
              <button type="button" className="bk-btn bk-btn--outline bk-btn--sm" onClick={() => setConfirming('new')}>Make a new code</button>
              <button type="button" className="bk-btn bk-btn--outline bk-btn--sm" onClick={() => setConfirming('off')}>Turn off</button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="child-signin__note">
            Make a code so {who} can see their progress, routines and diary when they're not at training.
            It keeps working until you turn it off.
          </p>
          <div className="child-signin__actions">
            <button type="button" className="bk-btn bk-btn--primary bk-btn--sm" disabled={busy} onClick={() => change('new')}>Make a code</button>
          </div>
        </>
      )}
      {error && <p className="bk-error">{error}</p>}
    </section>
  );
}
